import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Loader2 } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, Button } from '../components/ds';
import { paymentService } from '../services';

const FONT = "'IBM Plex Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

// Nach dem Stripe-Checkout landet der User hier (success_url). Der Zugang wird per
// Webhook (checkout.session.completed) freigeschaltet — das kann ein paar Sekunden
// dauern, daher pollen wir den Abo-Status, bis hasAccess true ist.
const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 15; // ~30s, danach manueller Fallback

function OnboardingPage() {
  const navigate = useNavigate();
  const [timedOut, setTimedOut] = useState(false);
  const attemptsRef = useRef(0);

  const poll = useCallback(async () => {
    try {
      const { data } = await paymentService.getSubscription();
      if (data.hasAccess) {
        navigate('/dashboard', { replace: true });
        return true;
      }
    } catch {
      // Netzwerk-/Auth-Fehler ignorieren und weiter pollen (Webhook evtl. noch offen).
    }
    return false;
  }, [navigate]);

  useEffect(() => {
    let active = true;
    let timer;

    const tick = async () => {
      if (!active) return;
      const done = await poll();
      if (done || !active) return;
      attemptsRef.current += 1;
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();
    return () => { active = false; clearTimeout(timer); };
  }, [poll]);

  const retry = () => {
    attemptsRef.current = 0;
    setTimedOut(false);
    poll();
  };

  return (
    <Layout>
      <div style={{ padding: '80px 40px', maxWidth: 520, margin: '0 auto', fontFamily: FONT, textAlign: 'center' }}>
        <Card padding={36}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--brand-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShieldCheck size={28} color="var(--brand-600)" />
            </div>
          </div>

          {!timedOut ? (
            <>
              <h1 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#101623' }}>Vielen Dank!</h1>
              <p style={{ margin: '0 0 24px', fontSize: 14, color: '#4A5262', lineHeight: 1.5 }}>
                Wir richten Ihren Zugang ein. Das dauert nur einen Moment …
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#8A93A3', fontSize: 13 }}>
                <Loader2 size={16} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
                Zugang wird aktiviert
              </div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </>
          ) : (
            <>
              <h1 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#101623' }}>Fast geschafft</h1>
              <p style={{ margin: '0 0 24px', fontSize: 14, color: '#4A5262', lineHeight: 1.5 }}>
                Die Aktivierung dauert etwas länger als üblich. Ihre Zahlung war erfolgreich – Sie können den Status
                gleich erneut prüfen oder direkt zum Dashboard gehen.
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button onClick={retry}>Status erneut prüfen</Button>
                <Button variant="secondary" onClick={() => navigate('/dashboard', { replace: true })}>Zum Dashboard</Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
}

export default OnboardingPage;
